/** When using goals/activities tables, we can use this context to share data that is needed in the various goal related components
 */

import { sendUpdate, useSubscribeTopicWithHandler } from 'fox-extensions';
import { createContext, useContext, useRef } from 'react';
import { useContentContext } from './content-context';
import {
	SwiftFoxGoalPointsCategory,
	SwiftFoxGoalsConfig,
	SwiftFoxGoalsGoal,
	SwiftFoxGoalsPointsSnapshotResult
} from './Membership/goals/contracts';
import {
	useGoalPointsCategories,
	useGoalsByOwnerType,
	useGoalsConfig,
	useGoalsSnapShot
} from './Membership/query-hooks/query-implementations';
import { GOALS_DATA_FETCHED, REFETCH_GOALS } from './topics';

type GoalsContext = {
	getGoalPointsCategories: (org: boolean) => SwiftFoxGoalPointsCategory[];
	getGoalById: (id: string, org: boolean) => SwiftFoxGoalsGoal | undefined;
	getGoalsConfig: () => SwiftFoxGoalsConfig;
	getGoalsSnapShot: (org: boolean) => SwiftFoxGoalsPointsSnapshotResult;
	loading: boolean;
	error: string;
};

const Context = createContext<GoalsContext | undefined>(undefined);
Context.displayName = 'GoalsContext';

export function GoalsContextProvider({
	children,
	usePersonGoalsData,
	useLinkedOrganisationGoalsData
}: {
	usePersonGoalsData: boolean;
	useLinkedOrganisationGoalsData: boolean;
	children: React.ReactNode;
}) {
	const { contentId, requestToken, isLoggedIn } = useContentContext();

	const goalPointsCategoriesRef = useRef<SwiftFoxGoalPointsCategory[] | undefined>(undefined);
	const orgGoalPointsCategoriesRef = useRef<SwiftFoxGoalPointsCategory[] | undefined>(undefined);

	const goalsByOwnerTypeRef = useRef<SwiftFoxGoalsGoal[] | undefined>(undefined);
	const orgGoalsByOwnerTypeRef = useRef<SwiftFoxGoalsGoal[] | undefined>(undefined);

	const goalsConfigRef = useRef<SwiftFoxGoalsConfig | undefined>(undefined);

	const goalsSnapShotRef = useRef<SwiftFoxGoalsPointsSnapshotResult | undefined>(undefined);
	const orgGoalsSnapShotRef = useRef<SwiftFoxGoalsPointsSnapshotResult | undefined>(undefined);

	const { error: goalPointsCategoriesError, loading: goalPointsCategoriesLoading } = useGoalPointsCategories({
		contentId,
		requestToken,
		skip: !isLoggedIn || !usePersonGoalsData,
		onFetchComplete: (data: SwiftFoxGoalPointsCategory[]) => {
			goalPointsCategoriesRef.current = data;
		}
	});

	const { error: orgGoalPointsCategoriesError, loading: orgGoalPointsCategoriesLoading } = useGoalPointsCategories({
		contentId,
		requestToken,
		useLinkedOrganisationData: true,
		skip: !isLoggedIn || !useLinkedOrganisationGoalsData,
		onFetchComplete: (data: SwiftFoxGoalPointsCategory[]) => {
			orgGoalPointsCategoriesRef.current = data;
		}
	});

	const { error: goalsByOwnerTypeError, loading: goalsByOwnerTypeLoading } = useGoalsByOwnerType({
		contentId,
		requestToken,
		skip: !isLoggedIn || !usePersonGoalsData,
		onFetchComplete: (data: SwiftFoxGoalsGoal[]) => {
			goalsByOwnerTypeRef.current = data;
		}
	});

	const { error: orgGoalsByOwnerTypeError, loading: orgGoalsByOwnerTypeLoading } = useGoalsByOwnerType({
		contentId,
		requestToken,
		useLinkedOrganisationData: true,
		skip: !isLoggedIn || !useLinkedOrganisationGoalsData,
		onFetchComplete: (data: SwiftFoxGoalsGoal[]) => {
			orgGoalsByOwnerTypeRef.current = data;
		}
	});

	const {
		error: orgSnapShotError,
		loading: orgSnapShotLoading,
		refetch: orgSnapShotRefetch
	} = useGoalsSnapShot({
		contentId,
		requestToken,
		useLinkedOrganisationData: true,
		skip: !useLinkedOrganisationGoalsData,
		onFetchComplete: (data: SwiftFoxGoalsPointsSnapshotResult) => (orgGoalsSnapShotRef.current = data)
	});

	const {
		error: snapShotError,
		loading: snapShotLoading,
		refetch: snapShotRefetch
	} = useGoalsSnapShot({
		contentId,
		requestToken,
		skip: !usePersonGoalsData,
		onFetchComplete: (data: SwiftFoxGoalsPointsSnapshotResult) => (goalsSnapShotRef.current = data)
	});

	const { error: goalsConfigError, loading: goalsConfigLoading } = useGoalsConfig({
		contentId,
		requestToken,
		skip: !isLoggedIn || (!useLinkedOrganisationGoalsData && !usePersonGoalsData),
		onFetchComplete: (data: SwiftFoxGoalsConfig) => (goalsConfigRef.current = data)
	});

	const getGoalPointsCategories = (org: boolean) =>
		org ? orgGoalPointsCategoriesRef.current ?? [] : goalPointsCategoriesRef.current ?? [];

	const getGoalById = (id: string, org: boolean) =>
		(org ? orgGoalsByOwnerTypeRef.current ?? [] : goalsByOwnerTypeRef.current ?? []).find(g => g.id === id);

	const getGoalsConfig = () => goalsConfigRef.current;

	const getGoalsSnapShot = (org: boolean) => (org ? orgGoalsSnapShotRef.current : goalsSnapShotRef.current);

	useSubscribeTopicWithHandler(REFETCH_GOALS, async () => {
		if (usePersonGoalsData) {
			await snapShotRefetch();
		}
		if (useLinkedOrganisationGoalsData) {
			await orgSnapShotRefetch();
		}
		sendUpdate(GOALS_DATA_FETCHED);
	});

	const contextValue: GoalsContext = {
		getGoalPointsCategories,
		getGoalById,
		getGoalsConfig,
		getGoalsSnapShot,
		loading:
			orgGoalsByOwnerTypeLoading ||
			goalsByOwnerTypeLoading ||
			orgGoalPointsCategoriesLoading ||
			goalPointsCategoriesLoading ||
			snapShotLoading ||
			orgSnapShotLoading ||
			goalsConfigLoading,
		error:
			orgGoalsByOwnerTypeError ||
			goalsByOwnerTypeError ||
			orgGoalPointsCategoriesError ||
			goalPointsCategoriesError ||
			goalsConfigError ||
			orgSnapShotError ||
			snapShotError
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useGoalsContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing goals context');

	return context;
}
